import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import LargePopupModalContainer from "../../../Containers/LargePopupModalContainer";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { Colors } from "../../../../utils/colors";
import { Event, EventType, MassTextAttachmentType } from "@markit/common.types";
import {
  isEventRelatedType,
  isProductRelatedType,
  sortProductsAndEventsList,
  userRelatedProducts,
} from "@markit/common.utils";
import filter from "lodash.filter";
import AttachmentSelectItemList from "./AttachmentSelectItemList";
import RectangleButton from "../../../Buttons/RectangleButton";
import LargePopupModalHeader from "../../../Containers/LargePopupModalHeader";
import SearchBoxContainer from "../../../Containers/SearchBoxContainer";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import { getUserWishlistSnap } from "../../../../utils/FirebaseUtils";
import { getEventState } from "../../../../redux/slices/eventSlice";
import { CircularProgress } from "@mui/material";
import ConfirmDeleteModal from "../../../Containers/ConfirmPopups/ConfirmDeleteModal";
import ListTabs from "../../../DropdownsAndTabs/ListTabs";

type AttachmentTicketLinkItemPanelProps = {
  closeOnPress: () => void;
  finalizeAttachment: (itemId: string) => void;
  userId: string; // the uid of the user we are sending the ticket link to
  attachmentType: MassTextAttachmentType;
};

// Side modal to help select a ticket or order link that the attendee has purchased
const AttachmentTicketLinkItemPanel = (
  props: AttachmentTicketLinkItemPanelProps
) => {
  const { closeOnPress, finalizeAttachment, userId, attachmentType } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { events, tickets } = useSelector(getEventState).events;
  const [searchTerm, setSearchTerm] = useState("");
  const [userProducts, setUserProducts] = useState<Event[]>([]);
  const [loadingTicketLinks, setLoadingTicketLinks] = useState(true);
  const [itemId, setItemId] = useState<string | undefined>();
  const [showConfirmBackModal, setShowConfirmBackModal] = useState(false);
  const [stateValue, setStateValue] = useState(0);

  useAsyncOnMount(async () => {
    // change this to backend function in future since it's in both webapp and frontend
    // get the conversation user's events that they're attending of the host
    const wishlistSnap = await getUserWishlistSnap(userId);
    const eventIds = new Set(
      wishlistSnap.docs.map((doc) => doc.data().eventId)
    );

    const definedProducts = userRelatedProducts(
      events,
      accountData.uid,
      tickets
    ).filter(
      (product) =>
        (attachmentType === MassTextAttachmentType.CALENDAR_INVITE
          ? isEventRelatedType(product.eventType)
          : isProductRelatedType(product.eventType)) && eventIds.has(product.id)
    );
    setUserProducts(definedProducts);
    setLoadingTicketLinks(false);
  });

  const userItems = useMemo(() => {
    return sortProductsAndEventsList(
      userProducts,
      stateValue === 0 ? "active" : "inactive"
    );
  }, [stateValue, userProducts]);

  const handleSearch = useCallback((text: string) => {
    setSearchTerm(text.toLowerCase());
  }, []);

  const containsItem = useCallback((item: Event, query: string) => {
    return item.title.toLowerCase().includes(query);
  }, []);

  const itemsToShow: Event[] = useMemo(() => {
    let itemList: Event[] = userItems;
    itemList = filter(itemList, (item: Event) => {
      return containsItem(item, searchTerm.toLowerCase());
    });
    return itemList;
  }, [userItems, searchTerm, containsItem]);

  const attachOnPress = useCallback(() => {
    if (itemId) {
      finalizeAttachment(itemId);
    }
  }, [finalizeAttachment, itemId]);

  const backOnPress = useCallback(() => {
    if (itemId) {
      setShowConfirmBackModal(true);
    } else {
      closeOnPress();
    }
  }, [itemId, closeOnPress]);

  return (
    <>
      <LargePopupModalContainer
        showModal={true}
        preventContainerScroll
        headerComp={
          <LargePopupModalHeader
            title={
              attachmentType === MassTextAttachmentType.TICKET_LINK
                ? "Attach Order Link"
                : "Attach Calendar Invite"
            }
            backOnPress={backOnPress}
            headerRight={
              <RectangleButton
                buttonLabel={"Add"}
                onPress={attachOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                disabled={!itemId}
                containerStyles={{
                  borderRadius: 100,
                  padding: "10px 14px",
                  height: 36,
                }}
              />
            }
          />
        }
        valueComp={
          <div
            className="ColumnNormal PreventScroll"
            style={{ height: "100%", gap: 10, flex: 1 }}
          >
            <SearchBoxContainer
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              containerStyles={{ marginTop: 0 }}
            />
            <ListTabs
              tabLabels={["Active", "Inactive"]}
              tabNumbers={[-1, -1]}
              selectedValue={stateValue}
              onChange={setStateValue}
              altColor={Colors.BLACK}
            />
            <div
              className="ColumnNormal HideScrollbar"
              style={{ height: "calc(100vh - 160px)", overflow: "auto" }}
            >
              {loadingTicketLinks ? (
                <div className="Centering" style={{ paddingTop: 100 }}>
                  <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
                </div>
              ) : (
                <AttachmentSelectItemList
                  items={itemId ? [itemId] : []}
                  setItems={(newItem: string) => {
                    if (itemId === newItem) {
                      setItemId(undefined);
                    } else {
                      setItemId(newItem);
                    }
                  }}
                  itemsToShow={itemsToShow}
                  stateValue={stateValue}
                  searchTerm={searchTerm}
                  selectedEventType={EventType.MARKIT}
                  showHiddenLabel={false}
                  isTicketLink={
                    attachmentType === MassTextAttachmentType.TICKET_LINK ||
                    attachmentType === MassTextAttachmentType.CALENDAR_INVITE
                  }
                />
              )}
            </div>
          </div>
        }
      />
      <ConfirmDeleteModal
        heading="If you go back, your changes will be discarded."
        subtext="Continue?"
        deleteButtonText="Discard Changes"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={backOnPress}
      />
    </>
  );
};

export default AttachmentTicketLinkItemPanel;
